import React, { useEffect } from "react";
import {
  Modal,
  Card,
  Form,
  Input,
  Button,
  Checkbox,
  message,
  Row,
  Col,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { configService } from "../services";
import { EInvoiceConfig, MedicalFacility, MedicalFacilityConfigRequest } from "../types";
import "./FacilityConfigModal.css";

interface FacilityConfigModalProps {
  visible: boolean;
  facility: MedicalFacility | null;
  onClose: () => void;
}

const FacilityConfigModal: React.FC<FacilityConfigModalProps> = ({
  visible,
  facility,
  onClose,
}) => {
  const [nationalPrescriptionForm] = Form.useForm();
  const [clinicInvoiceForm] = Form.useForm();
  const [pharmacyInvoiceForm] = Form.useForm();
  const queryClient = useQueryClient();
  const facilityId = facility?.id ?? 0;

  const { data: nationalRxConfig } = useQuery({
    queryKey: ["admin-national-rx-config", facilityId],
    queryFn: () => configService.getAdminNationalRxConfig(facilityId),
    enabled: visible && facilityId > 0,
  });

  const { data: clinicEInvoiceConfig } = useQuery({
    queryKey: ["admin-einvoice-config", facilityId, "medical_facility"],
    queryFn: () => configService.getAdminEInvoiceConfig(facilityId, "medical_facility"),
    enabled: visible && facilityId > 0,
  });

  const { data: pharmacyEInvoiceConfig } = useQuery({
    queryKey: ["admin-einvoice-config", facilityId, "pharmacy"],
    queryFn: () => configService.getAdminEInvoiceConfig(facilityId, "pharmacy"),
    enabled: visible && facilityId > 0,
  });

  const saveNationalPrescriptionMutation = useMutation({
    mutationFn: (values: Omit<MedicalFacilityConfigRequest, "medical_facility">) => {
      return configService.saveAdminNationalRxConfig(facilityId, values);
    },
    onSuccess: () => {
      message.success("Lưu cấu hình đơn thuốc quốc gia thành công!");
      queryClient.invalidateQueries({ queryKey: ["admin-national-rx-config", facilityId] });
    },
    onError: () => {
      message.error("Lưu cấu hình đơn thuốc quốc gia thất bại!");
    },
  });

  const saveClinicInvoiceMutation = useMutation({
    mutationFn: (values: EInvoiceConfig) => {
      return configService.saveAdminEInvoiceConfig(facilityId, {
        ...values,
        type: "medical_facility",
      });
    },
    onSuccess: () => {
      message.success("Lưu cấu hình hóa đơn điện tử phòng khám thành công!");
      queryClient.invalidateQueries({
        queryKey: ["admin-einvoice-config", facilityId, "medical_facility"],
      });
    },
    onError: () => {
      message.error("Lưu cấu hình hóa đơn điện tử phòng khám thất bại!");
    },
  });

  const savePharmacyInvoiceMutation = useMutation({
    mutationFn: (values: EInvoiceConfig) => {
      return configService.saveAdminEInvoiceConfig(facilityId, {
        ...values,
        type: "pharmacy",
      });
    },
    onSuccess: () => {
      message.success("Lưu cấu hình hóa đơn điện tử nhà thuốc thành công!");
      queryClient.invalidateQueries({
        queryKey: ["admin-einvoice-config", facilityId, "pharmacy"],
      });
    },
    onError: () => {
      message.error("Lưu cấu hình hóa đơn điện tử nhà thuốc thất bại!");
    },
  });

  useEffect(() => {
    if (visible && nationalRxConfig?.data?.data) {
      const d = nationalRxConfig.data.data;
      nationalPrescriptionForm.setFieldsValue({
        pk_code: d.pk_code,
        pk_username: d.pk_username,
        rx_prefix: d.rx_prefix,
        is_enabled: d.is_enabled,
      });
    }
  }, [visible, nationalPrescriptionForm, nationalRxConfig]);

  useEffect(() => {
    if (visible && clinicEInvoiceConfig?.data?.data) {
      const d = clinicEInvoiceConfig.data.data;
      clinicInvoiceForm.setFieldsValue({
        api_server: d.api_server,
        username: d.username,
        dai_gtgt: d.dai_gtgt,
        is_enabled: d.is_enabled,
      });
    }
  }, [visible, clinicInvoiceForm, clinicEInvoiceConfig]);

  useEffect(() => {
    if (visible && pharmacyEInvoiceConfig?.data?.data) {
      const d = pharmacyEInvoiceConfig.data.data;
      pharmacyInvoiceForm.setFieldsValue({
        api_server: d.api_server,
        username: d.username,
        dai_gtgt: d.dai_gtgt,
        is_enabled: d.is_enabled,
      });
    }
  }, [visible, pharmacyInvoiceForm, pharmacyEInvoiceConfig]);

  const onNationalPrescriptionFinish = (values: any) => {
    saveNationalPrescriptionMutation.mutate(values);
  };

  const onClinicInvoiceFinish = (values: any) => {
    saveClinicInvoiceMutation.mutate(values);
  };

  const onPharmacyInvoiceFinish = (values: any) => {
    savePharmacyInvoiceMutation.mutate(values);
  };

  const handleClose = () => {
    nationalPrescriptionForm.resetFields();
    clinicInvoiceForm.resetFields();
    pharmacyInvoiceForm.resetFields();
    onClose();
  };

  return (
    <Modal
      title={facility ? `Cấu hình cơ sở y tế: ${facility.name}` : "Cấu hình cơ sở y tế"}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <div className="facility-config-modal-content">
        <Row gutter={16} className="facility-config-section">
          <Col span={8}>
            <Card title="Cấu hình đơn thuốc quốc gia" className="config-section">
              <Form
                form={nationalPrescriptionForm}
                layout="vertical"
                onFinish={onNationalPrescriptionFinish}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="pk_code"
                      label="Mã liên thông PK"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập mã liên thông PK!",
                        },
                      ]}
                    >
                      <Input placeholder="Nhập mã liên thông PK" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="pk_username"
                      label="Tài khoản PK"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập tài khoản PK!",
                        },
                      ]}
                    >
                      <Input placeholder="Nhập tài khoản PK" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item name="pk_password" label="Mật khẩu PK">
                      <Input.Password placeholder="Nhập mật khẩu PK" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="rx_prefix"
                      label="Tiền tố mã toa"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập tiền tố mã toa!",
                        },
                      ]}
                    >
                      <Input placeholder="Nhập tiền tố mã toa" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="is_enabled" valuePropName="checked">
                  <Checkbox>Hiển thị chức năng</Checkbox>
                </Form.Item>

                <div className="section-actions">
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => nationalPrescriptionForm.submit()}
                    loading={saveNationalPrescriptionMutation.isPending}
                  >
                    Lưu thông tin
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>

          <Col span={8}>
            <Card
              title="Cấu hình hóa đơn điện tử phòng khám"
              className="config-section"
            >
              <Form
                form={clinicInvoiceForm}
                layout="vertical"
                onFinish={onClinicInvoiceFinish}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="api_server"
                      label="API Server"
                      rules={[
                        { required: true, message: "Vui lòng nhập API Server!" },
                      ]}
                    >
                      <Input placeholder="Nhập API Server" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="username"
                      label="Tài khoản"
                      rules={[
                        { required: true, message: "Vui lòng nhập tài khoản!" },
                      ]}
                    >
                      <Input placeholder="Nhập tài khoản" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item name="password" label="Mật khẩu">
                      <Input.Password placeholder="Nhập mật khẩu" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="dai_gtgt"
                      label="Dải GTGT"
                      rules={[
                        { required: true, message: "Vui lòng nhập dải GTGT!" },
                      ]}
                    >
                      <Input placeholder="Nhập dải GTGT" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="is_enabled" valuePropName="checked">
                  <Checkbox>Hiển thị chức năng</Checkbox>
                </Form.Item>

                <div className="section-actions">
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => clinicInvoiceForm.submit()}
                    loading={saveClinicInvoiceMutation.isPending}
                  >
                    Lưu thông tin
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>

          <Col span={8}>
            <Card
              title="Cấu hình hóa đơn điện tử nhà thuốc"
              className="config-section"
            >
              <Form
                form={pharmacyInvoiceForm}
                layout="vertical"
                onFinish={onPharmacyInvoiceFinish}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="api_server"
                      label="API Server"
                      rules={[
                        { required: true, message: "Vui lòng nhập API Server!" },
                      ]}
                    >
                      <Input placeholder="Nhập API Server" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="username"
                      label="Tài khoản"
                      rules={[
                        { required: true, message: "Vui lòng nhập tài khoản!" },
                      ]}
                    >
                      <Input placeholder="Nhập tài khoản" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item name="password" label="Mật khẩu">
                      <Input.Password placeholder="Nhập mật khẩu" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="dai_gtgt"
                      label="Dải GTGT"
                      rules={[
                        { required: true, message: "Vui lòng nhập dải GTGT!" },
                      ]}
                    >
                      <Input placeholder="Nhập dải GTGT" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="is_enabled" valuePropName="checked">
                  <Checkbox>Hiển thị chức năng</Checkbox>
                </Form.Item>

                <div className="section-actions">
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => pharmacyInvoiceForm.submit()}
                    loading={savePharmacyInvoiceMutation.isPending}
                  >
                    Lưu thông tin
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default FacilityConfigModal;
